import * as React from 'react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField,
    BooleanField,
    DateField,
    ReferenceField,
    Pagination,
    TopToolbar,
    sanitizeListRestProps,
    EditButton,
    useListContext,
} from 'react-admin';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Drawer, TextField as MuiTextField } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import OrderDetailEdit from './OrderDetailEdit';
import OrderDetailListFilterForm from './OrderDetailListFilterForm';
import { Box, Chip, Button } from '@mui/material';
import { ColorField } from '../ColorComponents';
import { stripedDataGridSx } from '../styles/dataGridStyles';

const DateRangeFilter = () => {
    const { filterValues, setFilters } = useListContext();
    const [startDate, setStartDate] = useState(filterValues.sd || '');
    const [endDate, setEndDate] = useState(filterValues.ed || '');

    useEffect(() => {
        setStartDate(filterValues.sd || '');
        setEndDate(filterValues.ed || '');
    }, [filterValues.sd, filterValues.ed]);

    const handleApply = () => {
        const newFilters = { ...filterValues };
        if (startDate) newFilters.sd = startDate;
        else delete newFilters.sd;
        if (endDate) newFilters.ed = endDate;
        else delete newFilters.ed;
        setFilters(newFilters, null);
    };

    const handleClear = () => {
        setStartDate('');
        setEndDate('');
        const newFilters = { ...filterValues };
        delete newFilters.sd;
        delete newFilters.ed;
        setFilters(newFilters, null);
    };

    const isActive = filterValues.sd || filterValues.ed;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MuiTextField label="Start Date" type="date" size="small" value={startDate}
                onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <MuiTextField label="End Date" type="date" size="small" value={endDate}
                onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <Button variant="contained" size="small" onClick={handleApply} sx={{ minWidth: 'auto', px: 2 }}>Apply</Button>
            {isActive && (
                <Button variant="outlined" size="small" onClick={handleClear} sx={{ minWidth: 'auto', px: 1 }}>Clear</Button>
            )}
        </Box>
    );
};

const OrderDetailListPagination = props => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <DateRangeFilter />
        <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />
    </Box>
);

const QuickFilters = () => {
    const { filterValues, setFilters } = useListContext();

    const handleFilterClick = (newFilters) => {
        setFilters(newFilters, null);
    };

    const isActiveFilter = (filterKey, filterValue) => {
        return filterValues[filterKey] === filterValue;
    };

    const clearFilters = () => {
        setFilters({}, null);
    };

    return (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Box sx={{ fontWeight: 'bold', mr: 1 }}>Quick Filters:</Box>

            <Chip
                label="Not Invoiced"
                icon={<CheckBoxOutlineBlankIcon />}
                onClick={() => handleFilterClick({ Not_Invoiced: true })}
                color={isActiveFilter('Not_Invoiced', true) ? 'primary' : 'default'}
                variant={isActiveFilter('Not_Invoiced', true) ? 'filled' : 'outlined'}
            />
            <Chip
                label="Canceled"
                icon={<CheckBoxIcon />}
                onClick={() => handleFilterClick({ Canceled: true })}
                color={isActiveFilter('Canceled', true) ? 'primary' : 'default'}
                variant={isActiveFilter('Canceled', true) ? 'filled' : 'outlined'}
            />

            {Object.keys(filterValues).length > 0 && (
                <Chip
                    label="Clear All"
                    onClick={clearFilters}
                    color="default"
                    variant="outlined"
                    sx={{ ml: 1 }}
                />
            )}
        </Box>
    );
};

const OrderDetailListActions = (props) => {
  const {
    exporter,
    filters,
    maxResults,
    setFilters,
    ...rest
  } = props;
  return (
    <TopToolbar {...sanitizeListRestProps(rest)} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <Box display="flex" justifyContent="flex-end" width="100%">
        <OrderDetailListFilterForm />
      </Box>
      <Box display="flex" justifyContent="flex-end" width="100%">
        <OrderDetailListPagination />
      </Box>
    </TopToolbar>
  );
};

const OrderDetailList = props => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    // Check if we're on an edit route
    const isMatch = !!(
        params.id &&
        params.id !== 'create' &&
        location.pathname.includes('/orderdetails/')
    );

    const handleClose = useCallback(() => {
        navigate('/orderdetails');
    }, [navigate]);

    return (
        <div sx={{ display: 'flex', fontSize: '0.75rem !important' }}>
            <Fragment>
                            <List
                                title="Order Reconciliation"
                                sort={{ field: 'Order_Num', order: 'DESC' }}
                                perPage={100}
                                pagination={<OrderDetailListPagination />}
                                sx={{
                                    flexGrow: 1,
                                    marginRight: 0,
                                }}
                                bulkActionButtons={false}
                                actions={<OrderDetailListActions/>}
                                {...props}
                            >
                                <QuickFilters />
                                <Datagrid
                                    rowClick="edit"
                                    sx={{
                                        ...stripedDataGridSx,
                                        "& .RaDatagrid-rowCell": {
                                            padding: '1px 4px 1px 2px;',
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.75rem !important',
                                            overflow: 'hidden',
                                            textOverflow:'ellipsis',
                                            maxWidth: '180px'
                                        },
                                    }}
                                    optimized
                                >
                                    <ColorField label="" source="Mill_Color" showText={false} />
                                    <ReferenceField label="Order #" source="OrderID" reference="orders" link="edit">
                                        <TextField source="Order_Num" />
                                    </ReferenceField>
                                    <ReferenceField label="Product" source="ProductID" reference="products" link={false}>
                                        <TextField source="Product" />
                                    </ReferenceField>
                                    <ReferenceField label="Pickup Location" source="PickupLocationID" reference="pickuplocations" link={false}>
                                        <TextField source="Pickup_Location" />
                                    </ReferenceField>
                                    <ReferenceField label="Customer" source="CustomerID" reference="customers" sortable={false} link={false}>
                                        <TextField source="Customer" />
                                    </ReferenceField>
                                    <NumberField label="QTY Ordered" source="Quantity" />
                                    <NumberField label="QTY Loaded" source="Qty_Loaded" />
                                    <TextField label="INV" source="Invoice_Num" />
                                    <BooleanField label="CNL" source="Canceled" TrueIcon={CheckBoxIcon} FalseIcon={CheckBoxOutlineBlankIcon} sx={{ '& .MuiSvgIcon-root': { color: '#d32f2f' } }} />
                                    <DateField source="Delivery_Date" />
                                    <ReferenceField label="Trucking Company" source="CarrierID" reference="truckingcompanies" link={false}>
                                        <TextField source="Trucking_Company" />
                                    </ReferenceField>
                                    <TextField label="Billing Notes" source="Billing_Notes" />
                                    <EditButton />
                                </Datagrid>
                            </List>
                <Drawer
                    variant="persistent"
                    open={isMatch}
                    anchor="right"
                    onClose={handleClose}
                    sx={{
                        zIndex: 100,
                    }}
                >
                    {/* To avoid any errors if the route does not match, we don't render at all the component in this case */}
                    {isMatch ? (
                        <OrderDetailEdit
                            id={params.id}
                            onCancel={handleClose}
                            {...props}
                        />
                    ) : null}
                </Drawer>
            </Fragment>
        </div>
    );
};

export default OrderDetailList;
